import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Check, Calendar, Flag, Users, Clock, 
  ChevronDown, ChevronRight, Trash2, Edit2, X,
  AlertCircle, Circle, CheckCircle2
} from 'lucide-react';
import { Button } from './button';
import { Input } from './input';

// Priority levels with Apple Reminders style
const PRIORITIES = {
  none: { label: 'None', color: 'text-slate-400', bg: 'bg-slate-500/20', flag: null },
  low: { label: 'Low', color: 'text-blue-400', bg: 'bg-blue-500/20', flag: '!' },
  medium: { label: 'Medium', color: 'text-amber-400', bg: 'bg-amber-500/20', flag: '!!' },
  high: { label: 'High', color: 'text-red-400', bg: 'bg-red-500/20', flag: '!!!' }
};

const SAMPLE_PARTICIPANTS = [
  { id: '1', name: 'You', initials: 'ME', color: 'bg-indigo-500' },
  { id: '2', name: 'Sarah', initials: 'SK', color: 'bg-emerald-500' },
  { id: '3', name: 'Mike', initials: 'MJ', color: 'bg-amber-500' },
  { id: '4', name: 'Alex', initials: 'AT', color: 'bg-purple-500' },
];

const RemindersPanel = ({ 
  reminders = [], 
  onAddReminder, 
  onUpdateReminder, 
  onDeleteReminder,
  onToggleComplete,
  participants = SAMPLE_PARTICIPANTS,
  theme = 'dark', // 'dark' for startup studio, 'earth' for compass
  className = ''
}) => {
  const [localReminders, setLocalReminders] = useState(reminders);
  const [isAdding, setIsAdding] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '',
    notes: '',
    priority: 'none',
    dueDate: '',
    assignedTo: [],
    completed: false
  });
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, today, upcoming, completed

  useEffect(() => {
    setLocalReminders(reminders);
  }, [reminders]);

  const handleAdd = () => {
    if (!newReminder.title.trim()) return;
    
    const reminder = {
      id: Date.now().toString(),
      ...newReminder,
      createdAt: new Date().toISOString()
    };
    
    const updated = [...localReminders, reminder];
    setLocalReminders(updated);
    onAddReminder?.(reminder);
    
    setNewReminder({
      title: '',
      notes: '',
      priority: 'none',
      dueDate: '',
      assignedTo: [],
      completed: false
    });
    setIsAdding(false);
  };

  const toggleComplete = (id) => {
    const updated = localReminders.map(r => 
      r.id === id ? { ...r, completed: !r.completed } : r
    );
    setLocalReminders(updated);
    onToggleComplete?.(id);
  };

  const deleteReminder = (id) => {
    const updated = localReminders.filter(r => r.id !== id);
    setLocalReminders(updated);
    onDeleteReminder?.(id);
  };

  const toggleAssignee = (participantId) => {
    const current = newReminder.assignedTo || [];
    const updated = current.includes(participantId)
      ? current.filter(id => id !== participantId)
      : [...current, participantId];
    setNewReminder({ ...newReminder, assignedTo: updated });
  };

  // Filter reminders
  const filteredReminders = localReminders.filter(r => {
    if (filter === 'completed') return r.completed;
    if (filter === 'today') {
      const today = new Date().toDateString();
      return r.dueDate && new Date(r.dueDate).toDateString() === today;
    }
    if (filter === 'upcoming') {
      return r.dueDate && new Date(r.dueDate) > new Date() && !r.completed;
    }
    return !r.completed;
  });

  // Sort by priority and due date
  const sortedReminders = [...filteredReminders].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2, none: 3 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    if (a.dueDate && b.dueDate) {
      return new Date(a.dueDate) - new Date(b.dueDate);
    }
    return 0;
  });

  const themeClasses = theme === 'dark' 
    ? {
        bg: 'bg-white/5',
        border: 'border-white/10',
        text: 'text-white',
        textMuted: 'text-white/60',
        hover: 'hover:bg-white/10',
        input: 'bg-white/5 border-white/20 text-white placeholder:text-white/30'
      }
    : {
        bg: 'bg-[var(--primary)]/5',
        border: 'border-[var(--primary)]/20',
        text: 'text-[var(--fg)]',
        textMuted: 'text-[var(--fg)]/60',
        hover: 'hover:bg-[var(--muted)]/30',
        input: 'bg-white/80 border-[var(--muted)] text-[var(--fg)]'
      };

  return (
    <div className={`rounded-xl ${themeClasses.bg} backdrop-blur border ${themeClasses.border} ${className}`}>
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <CheckCircle2 size={14} className="text-blue-400" />
          </div>
          <span className={`text-sm font-semibold ${themeClasses.text}`}>Reminders</span>
          <span className={`text-xs ${themeClasses.textMuted}`}>
            ({sortedReminders.length})
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsAdding(true)}
          className={`h-7 px-2 ${themeClasses.textMuted} ${themeClasses.hover}`}
        >
          <Plus size={14} />
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 p-2 border-b border-white/5">
        {['all', 'today', 'upcoming', 'completed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
              filter === f 
                ? 'bg-blue-500/20 text-blue-400' 
                : `${themeClasses.textMuted} ${themeClasses.hover}`
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Add New Reminder Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-2 border-b border-white/10">
              <Input
                placeholder="New reminder..."
                value={newReminder.title}
                onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                className={`text-sm ${themeClasses.input}`}
                autoFocus
              />
              
              <div className="flex flex-wrap gap-2">
                {/* Priority Selector */}
                <div className="flex gap-1">
                  {Object.entries(PRIORITIES).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => setNewReminder({ ...newReminder, priority: key })}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                        newReminder.priority === key 
                          ? `${val.bg} ${val.color}` 
                          : `${themeClasses.textMuted} ${themeClasses.hover}`
                      }`}
                    >
                      {val.flag || '○'}
                    </button>
                  ))}
                </div>

                {/* Due Date */}
                <input
                  type="date"
                  value={newReminder.dueDate}
                  onChange={(e) => setNewReminder({ ...newReminder, dueDate: e.target.value })}
                  className={`px-2 py-1 rounded text-[10px] ${themeClasses.input} cursor-pointer`}
                />
              </div>

              {/* Assignees */}
              <div className="flex items-center gap-2">
                <span className={`text-[10px] ${themeClasses.textMuted}`}>Assign:</span>
                <div className="flex gap-1">
                  {participants.map(p => (
                    <button
                      key={p.id}
                      onClick={() => toggleAssignee(p.id)}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white transition-all ${
                        newReminder.assignedTo?.includes(p.id) 
                          ? `${p.color} ring-2 ring-white/50` 
                          : `${p.color} opacity-40 hover:opacity-70`
                      }`}
                    >
                      {p.initials}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleAdd}
                  className="flex-1 h-7 text-xs bg-blue-500 hover:bg-blue-600 text-white"
                >
                  Add Reminder
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsAdding(false)}
                  className={`h-7 px-2 ${themeClasses.textMuted}`}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reminders List */}
      <div className="max-h-[400px] overflow-y-auto">
        {sortedReminders.length === 0 ? (
          <div className={`p-6 text-center ${themeClasses.textMuted}`}>
            <Circle size={24} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">No reminders</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {sortedReminders.map(reminder => {
              const priority = PRIORITIES[reminder.priority] || PRIORITIES.none;
              const isOverdue = reminder.dueDate && new Date(reminder.dueDate) < new Date() && !reminder.completed;
              const assignedParticipants = participants.filter(p => reminder.assignedTo?.includes(p.id));

              return (
                <motion.div
                  key={reminder.id}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`group p-2 ${themeClasses.hover} transition-colors`}
                >
                  <div className="flex items-start gap-2">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleComplete(reminder.id)}
                      className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        reminder.completed 
                          ? 'bg-emerald-500 border-emerald-500' 
                          : `border-white/30 ${themeClasses.hover}`
                      }`}
                    >
                      {reminder.completed && <Check size={12} className="text-white" />}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${reminder.completed ? 'line-through opacity-50' : ''} ${themeClasses.text}`}>
                          {reminder.title}
                        </span>
                        {priority.flag && (
                          <span className={`text-[10px] font-bold ${priority.color}`}>
                            {priority.flag}
                          </span>
                        )}
                      </div>

                      {/* Meta info */}
                      <div className="flex items-center gap-2 mt-1">
                        {reminder.dueDate && (
                          <span className={`flex items-center gap-1 text-[10px] ${
                            isOverdue ? 'text-red-400' : themeClasses.textMuted
                          }`}>
                            <Calendar size={10} />
                            {new Date(reminder.dueDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        )}
                        
                        {/* Assigned avatars */}
                        {assignedParticipants.length > 0 && (
                          <div className="flex -space-x-1">
                            {assignedParticipants.slice(0, 3).map(p => (
                              <div
                                key={p.id}
                                className={`w-4 h-4 rounded-full ${p.color} flex items-center justify-center text-[7px] font-bold text-white ring-1 ring-white/20`}
                              >
                                {p.initials}
                              </div>
                            ))}
                            {assignedParticipants.length > 3 && (
                              <div className="w-4 h-4 rounded-full bg-slate-600 flex items-center justify-center text-[7px] font-bold text-white ring-1 ring-white/20">
                                +{assignedParticipants.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delete button (shows on hover) */}
                    <button
                      onClick={() => deleteReminder(reminder.id)}
                      className={`opacity-0 group-hover:opacity-100 p-1 rounded ${themeClasses.hover} transition-all`}
                    >
                      <Trash2 size={12} className="text-red-400" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export { RemindersPanel, PRIORITIES, SAMPLE_PARTICIPANTS };
export default RemindersPanel;
